import { all, call, fork, put, select, takeLatest } from "redux-saga/effects";
import {
    DELETE_DASHBOARD_USER,
    FETCH_DASHBOARD_USER_INITIAL_DATA,
    FETCH_DASHBOARD_USERS,
    MIGRATE_DASHBOARD_USERS,
    UPDATE_DASHBOARD_USER_FILTER
} from "../constants/dashboardUserConstants";
import {
    DeleteDashboardUserAction,
    FetchDashboardUsersAction,
    MigrateDashboardUsersAction,
    UpdateDashboardUserFilterAction
} from "../actions/dashboard.user.action";
import { fetchDashboardUsers, hideDashboardUserLoading, receiveDashboardUserInitialData, receiveDashboardUsers, showDashboardUserLoading } from "../actions/dashboardUser";
import { DatatableResponseInterface } from "../interfaces/datatable.response.interface";
import { UserInterface } from "../interfaces/user.interface";
import { catchException } from "./errorHandlerSaga";
import { DashboardUserDatatableRequestInterface } from "../interfaces/datatable.request.interface";
import { convertToDashboardUsersRequest, getNotificationFormat } from "../utils/helpers";
import { deleteDashboardUserAPI, fetchDashboardUserAPI, fetchManagementCompanies, migrateDashboardUsersAPI } from "../api/Auth0API";
import { sendNotification } from "../actions/notificationHandler";
import { DatatableRowInterface } from "../interfaces/datatable.row.interface";
import { watchSendSchedulePaymentsDeleteLink } from "./homeownerDetailsSaga";
import { RootState } from "../store/reducers";

/**
 * Saga to observe FETCH_DASHBOARD_USERS action
 */
export function* watchRequestDashboardUsersSaga() {
    yield takeLatest(FETCH_DASHBOARD_USERS, requestDashboardUsersSaga)
}

/**
 * Resolver for FETCH_DASHBOARD_USERS
 * @param action
 */
function* requestDashboardUsersSaga(action: FetchDashboardUsersAction) {
    try {
        yield put(showDashboardUserLoading)
        const response: DatatableResponseInterface = yield call(fetchDashboardUserAPI, action.request)
        yield put(receiveDashboardUsers(response))
    } catch (e) {
        console.warn('Error in requestDashboardUsersSaga: ', e)
        yield catchException(e)
    } finally {
        yield put(hideDashboardUserLoading)
    }

}

/**
 * Saga to observe UPDATE_DASHBOARD_USER_FILTER action
 */
export function* watchUpdateFiltersSaga() {
    yield takeLatest(UPDATE_DASHBOARD_USER_FILTER, updateFilterSaga);
}

/**
 * Resolver for UPDATE_DASHBOARD_USER_FILTER
 * @param action
 */
function* updateFilterSaga(action: UpdateDashboardUserFilterAction) {
    if (!action.cleanup) {
        const request: DashboardUserDatatableRequestInterface = convertToDashboardUsersRequest(action.payload);
        yield put(fetchDashboardUsers(request));
    } else {
        yield put(receiveDashboardUsers({
            offset: 0,
            limit: 0,
            itemCount: 0,
            items: []
        }))
    }
}

/**
 * Saga to observe FETCH_DASHBOARD_USER_INITIAL_DATA
 */
export function* watchFetchInitialDataSaga() {
    yield takeLatest(FETCH_DASHBOARD_USER_INITIAL_DATA, fetchInitialDataSaga);
}

/**
 * Resolve FETCH_DASHBOARD_USER_INITIAL_DATA
 */
function* fetchInitialDataSaga(): Generator<any, void, any> {
    try {
        const managementCompanies = yield call(fetchManagementCompanies);
        yield put(receiveDashboardUserInitialData(managementCompanies))
    } catch (e) {
        console.warn('Error in fetchInitialDataSaga: ', e)
        yield catchException(e)
    }
}

/**
 * Saga to observe DELETE_DASHBOARD_USER
 */
export function* watchDeletePaymentScheduled() {
    yield takeLatest(DELETE_DASHBOARD_USER, deleteDashboardUserSaga);
}

export function* watchMigrateDashboardUsersSaga() {
    yield takeLatest(MIGRATE_DASHBOARD_USERS, migrateDashboardUsersSaga);
}

/**
 * Resolve DELETE_DASHBOARD_USER
 */
function* deleteDashboardUserSaga(action: DeleteDashboardUserAction) {
    try {
        yield put(showDashboardUserLoading)
        yield call(deleteDashboardUserAPI, action.dashboardUserId)
        const request: DashboardUserDatatableRequestInterface = convertToDashboardUsersRequest(action.payload);
        yield put(fetchDashboardUsers(request));
        yield put(sendNotification(getNotificationFormat(
            'success',
            `Success! The user ${action.emailAddress} has been deleted.`
        )));
    } catch (e) {
        console.warn('Error in deleteDashboardUserSaga: ', e)
        yield catchException(e)
    }
    yield put(hideDashboardUserLoading)
}

function* migrateDashboardUsersSaga(action: MigrateDashboardUsersAction): Generator<any, void, any> {
    try {
        const userInfo: UserInterface = yield select((state: RootState) => state.userInfo);
        if (!userInfo?.isFSEmployee) {
            yield put(sendNotification(getNotificationFormat(
                'error',
                'We are not able to process your request, confirm if you have the correct access to proceed with the operation.'
            )));
            return;
        }

        yield put(showDashboardUserLoading)

        const request = {
            userIds: action.userIds || [],
            migrateAllFilteredUsers: action.migrateAllFilteredUsers,
            managementCompanyId: action.payload.managementCompanySelected?.id,
            emailQuery: action.payload.userEmailAddressQueryString || ''
        };

        const response = yield call(migrateDashboardUsersAPI, request);

        const dataRequest: DashboardUserDatatableRequestInterface = convertToDashboardUsersRequest(action.payload);
        yield put(fetchDashboardUsers(dataRequest));

        const message = response.failedCount > 0
            ? `WorkOS sync completed. ${response.migratedCount} migrated, ${response.failedCount} failed.`
            : `Success! ${response.migratedCount} users migrated to WorkOS.`;

        yield put(sendNotification(getNotificationFormat(
            response.failedCount > 0 ? 'error' : 'success',
            message
        )));
    } catch (e) {
        console.warn('Error in migrateDashboardUsersSaga: ', e)
        yield catchException(e)
    } finally {
        yield put(hideDashboardUserLoading)
    }
}

export function* dashboardUsersSagas() {
    yield all([
        fork(watchUpdateFiltersSaga),
        fork(watchFetchInitialDataSaga),
        fork(watchRequestDashboardUsersSaga),
        fork(watchDeletePaymentScheduled),
        fork(watchMigrateDashboardUsersSaga),
        fork(watchSendSchedulePaymentsDeleteLink)

    ]);
}
